import { inject, Injectable } from '@angular/core';
import { getRandomDateTime, getRandomInt, getRandomItem } from '@app/shared/utils/random';
import { IPosition } from '@app/core/interfaces/position.interface';
import { IArea, IAreaDetailed, IBoundaryNode } from '@app/api/interfaces/area.interface';
import { areasMockData } from '@mock/data/areas-mock.data';
import booleanDisjoint from '@turf/boolean-disjoint';
import { polygon } from '@turf/helpers';
import { Feature, Polygon } from 'geojson';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { IPaginationMeta } from '@app/api/interfaces/common/pagination-meta.interface';
import { MockService } from '@mock/mock.service';
import { ProjectsMockService } from '@mock/data-services/projects-mock.service';
import { IListFilter } from '@app/api/interfaces/common/list-filter';

@Injectable({ providedIn: 'root' })
export class AreasMockService extends MockService {
  private projectsMockService = inject(ProjectsMockService);

  public readonly areas: IAreaDetailed[];

  private boundaryNodeId = 1;
  private existingPolygons: Feature<Polygon>[] = [];
  private projectsCenters = new Map<string, IPosition>();

  constructor() {
    super();

    this.areas = this.isMockDataGenerated() ? this.generateAreas(this.dataGeneratedCount.areas) : areasMockData;
  }

  public getAll(projectId: string, filter: IListFilter): Observable<IApiResponse<IArea[], IPaginationMeta>> {
    const filteredAreas = this.areas.filter(area => area.projectId === projectId);

    const areas = filter['includeBoundaryNodes']
      ? filteredAreas
      : filteredAreas.map(area => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { boundaryNodes, ...all } = area;

          return {
            ...all,
          };
        });

    return this.mockResponse('AREAS:getALL', {
      data: areas,
      meta: { pagination: { count: areas.length, total: areas.length, offset: 0, limit: areas.length } },
    }).pipe(delay(this.requestDelay));
  }

  public resolveAreaId(index?: number) {
    return index && index > this.areas.length ? this.areas[index].id : this.areas[0].id;
  }

  private generateAreas(count: number): IAreaDetailed[] {
    const projectIds = this.projectsMockService.projects.map(item => item.id);

    return new Array(count).fill(null).map((_, i) => {
      const projectId = getRandomItem(projectIds);

      const area: IAreaDetailed = {
        id: `a_gen_${i}`,
        projectId,
        name: `Area ${projectId}-a_${i}`,
        loggerCount: { deployed: getRandomInt(0, 50), collected: getRandomInt(0, 50) },
        createdAt: getRandomDateTime(365, 100).toISOString(),
      };

      if (getRandomInt(0, 10) > 2) {
        area.lastLoginAt = getRandomDateTime(0, 50).toISOString();
      }

      if (getRandomInt(0, 10) > 2) {
        area.lastMeasurementAt = getRandomDateTime(0, 50).toISOString();
      }

      if (!this.projectsCenters.has(area.projectId)) {
        this.projectsCenters.set(area.projectId, {
          lat: this.mapCenter.lat + 0.5 * Math.cos(Math.random() * 2 * Math.PI),
          lon: this.mapCenter.lon + 0.5 * Math.sin(Math.random() * 2 * Math.PI),
        });
      }

      area.boundaryNodes = Array.from({ length: getRandomInt(1, 10) <= 3 ? getRandomInt(2, 3) : 1 }).map(() =>
        this.generateAreaBoundaryNodesWithoutRelationToLoggers(
          this.projectsCenters.get(area.projectId)!,
          2000,
          getRandomInt(5, 20),
          0.5,
          true
        )
      );

      return area;
    });
  }

  // Testing on dashboard
  private generateAreaBoundaryNodesWithoutRelationToLoggers(
    projectCenter: IPosition,
    radiusMeters: number,
    pointsCount: number,
    clusterFactor = 0.5,
    allowOverlap = true
  ): IBoundaryNode[] {
    const earthRadius = 6378137;
    const maxAttempts = 10;
    let attempt = 0;
    let center: IPosition;
    let coords: [number, number][] = [];
    let poly: Feature<Polygon> | null = null;

    while (attempt < maxAttempts) {
      attempt++;
      const angle = Math.random() * 2 * Math.PI;
      const distance = 0.2 * (1 - clusterFactor) + Math.random() * 0.2 * clusterFactor;

      center = {
        lat: projectCenter.lat + distance * Math.cos(angle),
        lon: projectCenter.lon + distance * Math.sin(angle),
      };

      coords = [];
      for (let i = 0; i < pointsCount; i++) {
        const anglePoint = (2 * Math.PI * i) / pointsCount;
        const randomRadius = radiusMeters * (0.7 + Math.random() * 0.6);

        const dx = randomRadius * Math.cos(anglePoint);
        const dy = randomRadius * Math.sin(anglePoint);

        const deltaLat = (dy / earthRadius) * (180 / Math.PI);
        const deltaLon = (dx / (earthRadius * Math.cos((Math.PI * center.lat) / 180))) * (180 / Math.PI);

        coords.push([center.lon + deltaLon, center.lat + deltaLat]);
      }
      coords.push(coords[0]);

      poly = polygon([coords]);

      if (allowOverlap || this.existingPolygons.every(p => booleanDisjoint(p, poly!))) {
        break;
      }
    }

    if (!poly) throw new Error('Generated polygon could not be created after maximum attempts');

    this.existingPolygons.push(poly);

    return poly.geometry.coordinates[0].map(c => ({
      id: (this.boundaryNodeId++).toString(),
      lon: c[0],
      lat: c[1],
    }));
  }
}
